import { FC, useEffect, useMemo, useRef, useState } from "react";
import "./Centres.scss";
import CustomButton from "../../../UI/Buttons/CustomButton";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
  getAllActiveCentresListThunk,
  getAllPendingEnquiriesThunk,
  getCentreDetailsThunk,
  getCentresUsersThunk,
  getEnquiryDetailsThunk,
} from "../../../../store/superadmin/thunks";
import Search from "../../../UI/Icons/Search";
import TextInput from "../../../UI/Inputs/TextInput";
import Chevron from "../../../UI/Icons/Chevron";
import RightArrow from "../../../UI/Icons/RightArrow";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import CentresAndUsers from "..";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { useNavigate } from "react-router";
import Pagination from "../../../Pagination/Pagination";
import {
  CENTRE_SORT_OPTIONS,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
  ITEMS_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
} from "../../../../constants/app";
import { clearAllPendingEnquiriesAction, resetCentrePicAction } from "../../../../store/superadmin/slice";
import PlaceHolderImage from "../../../UI/Icons/PlaceHolderImage";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import ReviewCentre from "./ReviewCentre";
import ViewCentre from "./ViewCentre";
import {
  IPendingEnquiry,
  TCenterDetails,
} from "../../../../store/superadmin/types";
import Roles from "../../../../models/roles";
import SortBySelect from "../../../UI/Inputs/SortBySelect";
import EditPencil from "../../../UI/Icons/EditPencil";
import View24 from "../../../UI/Icons/View24";

const AdminCentres: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getAllPendingEnquiries = useSelector(
    (state) => state.superadmin.allPendingEnquiriesList
  );
  const getAllActiveCentres = useSelector(
    (state) => state.superadmin.allActiveCentresList
  );
  const totalActiveCentresCount = useSelector(
    (state) => state.superadmin.totalActiveCentresCount
  );
  const totalPendingCentresCount = useSelector(
    (state) => state.superadmin.totalPendingCentresCount
  );
  const user = useSelector((state) => state.auth.user);

  const isSuperAdmin = useMemo(
    () => user?.role.name === Roles.SUPER_ADMIN,
    [user]
  );

  const isInitialLoad = useRef<boolean>(false);
  const [isOpen1, setisOpen1] = useState(true);
  const [isOpen2, setisOpen2] = useState(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [editCentreId, setEditCentreId] = useState<number | null>(0);
  const [pendingReviewPage, setPendingReviewPage] = useState<number>(1);
  const [totalPendingReviewPage, setTotalPendingReviewPage] =
    useState<number>(1);
  const [pendingReviewPaginationLinks, setPendingReviewPaginationLinks] =
    useState<any[]>([]);
  const [pendingReviewPerPage, setPendingReviewPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [activeReviewPage, setActiveReviewPage] = useState<number>(1);
  const [totalActiveReviewPage, setTotalActiveReviewPage] = useState<number>(1);
  const [activeReviewPaginationLinks, setActiveReviewPaginationLinks] =
    useState<any[]>([]);
  const [activeReviewPerPage, setActiveReviewPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );
  const isPendingReviewFetched = useRef(false);
  const [selectedSort, setSelectedSort] = useState<string>("business_name-asc");
  const [selectedSortBy, setSelectedSortBy] = useState<string>("business_name");
  const [selectedSortOrder, setSelectedSortOrder] = useState<string>("asc");

  const handleReviewEnquiry = async (id: number) => {
    await dispatch(resetCentrePicAction())
    dispatch(showTransparentPreloaderAction());
    localStorage.setItem("enquiry_id", String(id));
    await dispatch(getEnquiryDetailsThunk({ enquiry_id: id }));
    dispatch(hideTransparentPreloaderAction());
    setShowModal(!false);
  };

  const handleSelect = (selectedOption: any) => {};

  // const handleSorting = (opt: any) => {
  //   setSelectedSort(opt.value);
  // };

  const handleSorting = (selectedOption: any) => {
    let splitValue = selectedOption?.value?.split("-");
    setSelectedSort(selectedOption?.value);
    setSelectedSortBy(splitValue[0]);
    setSelectedSortOrder(splitValue[1]);
  };

  useEffect(() => {
    if (editCentreId !== 0) {
      localStorage.setItem("centre_id", String(editCentreId));
    }
  }, [editCentreId]);

  const convertDate = (dateStr: string) => {
    const date = new Date(dateStr);

    // Format the desired date
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    const formattedDate: string = date
      .toLocaleDateString("en-GB", options)
      .replaceAll(",", "");

    return formattedDate;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = async () => {
    setOpenDropdown(false);
    await dispatch(resetCentrePicAction())
    navigate(AUTH_ROUTE.ADMIN_EDIT_CENTRE);
  };

  const handleView = async (id: number) => {
    setOpenDropdown(false);
    dispatch(showTransparentPreloaderAction());
    await dispatch(getCentreDetailsThunk({ centre_id: id }));
    dispatch(hideTransparentPreloaderAction());
    setShowViewModal(true);
  };

  const handlePendingReviewPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("page");
      setPendingReviewPage(Number(page));
    }
  };

  const handleActiveReviewPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("page");
      setActiveReviewPage(Number(page));
    }
  };

  useEffect(() => {
    if (getAllPendingEnquiries !== null) {
      setPendingReviewPaginationLinks(getAllPendingEnquiries?.links);
      setTotalPendingReviewPage(getAllPendingEnquiries?.last_page);
    }
  }, [getAllPendingEnquiries]);

  useEffect(() => {
    if (getAllActiveCentres !== null) {
      setActiveReviewPaginationLinks(getAllActiveCentres?.links);
      setTotalActiveReviewPage(getAllActiveCentres?.last_page);
    }
  }, [getAllActiveCentres]);

  const fetchPendingReviewCentres = async () => {
    await dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllPendingEnquiriesThunk({
        keyword: selectedKeyword,
        page: pendingReviewPage,
        per_page: pendingReviewPerPage,
      })
    );
    fetchAllActiveCentresList();
    await dispatch(hideTransparentPreloaderAction());
  };

  const fetchAllActiveCentresList = async () => {
    await dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllActiveCentresListThunk({
        keyword: selectedKeyword,
        page: activeReviewPage,
        per_page: activeReviewPerPage,
        sort_by: selectedSortBy,
        sort_order: selectedSortOrder,
      })
    );
    fetchUsers();
    await dispatch(hideTransparentPreloaderAction());
  };

  const fetchUsers = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCentresUsersThunk({
        keyword: "",
        centre_admin_page: "",
        centre_user_page: "",
        centre_admin_per_page: DEFAULT_ITEMS_PER_PAGE,
        centre_user_per_page: DEFAULT_ITEMS_PER_PAGE,
        status: "",
        centre_id: "",
        centre_admin_sort_by: "",
        centre_admin_sort_order: "",
        centre_user_sort_by: "",
        centre_user_sort_order: "",
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    const clearAllPendingEnquiries = () => {
      return () => {
        dispatch(clearAllPendingEnquiriesAction());
      };
    };
    clearAllPendingEnquiries();
  }, []);

  useEffect(() => {
    // Reset the flag each time the dependencies change
    isPendingReviewFetched.current = false;

    fetchPendingReviewCentres().then(() => {
      isPendingReviewFetched.current = true;
    });
  }, [selectedKeyword, pendingReviewPage, pendingReviewPerPage]);

  useEffect(() => {
    // const delay = !isCentreApiLoaded.current ? 0 : 1000 // when page loading there is delay of 1000 sec but when changing page it is 0 sec
    // const delayFetch = setTimeout(() => {
    if (isPendingReviewFetched.current) {
      fetchAllActiveCentresList();
    } // }, delay); // Add a 500ms delay // Getting Auth Failed error so based on API team comments added some time delay
  }, [selectedKeyword, activeReviewPage, activeReviewPerPage, selectedSort]);

  // Trigger API only when debounced value updates
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch);
      setPendingReviewPage(1);
      setActiveReviewPage(1);
    }
  }, [debouncedSearch]);

  const handleSearchValue = (value: string) => {
    setSearchInput(value);
  }

  

  return (
    <>
      <div className="d-flex flex-column gap-10">
        <CentresAndUsers create={"centres"} />
        <div className="d-flex gap-10 align-items-center justify-content-between">
          <div className="form-group w-200px mobile-w-100 input-icon icon-left">
            <Search />
            <TextInput
              field={"search"}
              label={"Search..."}
              onChange={(e) => handleSearchValue(e.target.value)}
              placeholder={" " + ""}
            />
          </div>
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="bg-alert-light py-8px px-15px radius-8px d-flex flex-column flex-lg-row gap-8 align-items-center">
            <div
              className="d-flex gap-8 align-items-center cursor-pointer"
              onClick={() => setisOpen1(!isOpen1)}
            >
              <Chevron isOpen={isOpen1} />
              <div className="fs-16 fw-700 text-black">
                {totalPendingCentresCount > 0 ? totalPendingCentresCount : 0}{" "}
                Pending Reviews
              </div>
            </div>
          </div>
          {isOpen1 && (
            <>
              {getAllPendingEnquiries &&
              getAllPendingEnquiries?.data.length > 0 ? (
                getAllPendingEnquiries?.data?.map(
                  (getAllPendingEnquiry: IPendingEnquiry, index: number) => (
                    <div
                      key={index}
                      className={`bg-white p-15px d-flex flex-column flex-lg-row gap-40 gap-mobile-10 align-items-start align-items-lg-center justify-content-between ${
                        totalPendingCentresCount > 1
                          ? "border-bottom border-off-white"
                          : ""
                      }`}
                    >
                      <div className="w-100 d-flex flex-column gap-3 fw-400 text-black text-break">
                        <div className="fs-16 fw-700 lh-16px">
                          {getAllPendingEnquiry.centre_infos?.[0].business_name}
                        </div>
                        <div className="fs-13">
                          {getAllPendingEnquiry.centre_infos?.[0].address}
                        </div>
                        <div className="fs-13 text-grey-medium">
                          {getAllPendingEnquiry.centre_infos?.length - 1 > 0
                            ? `+ ${
                                getAllPendingEnquiry.centre_infos?.length - 1
                              } other business`
                            : ""}
                        </div>
                      </div>
                      <div className="min-w-115px">
                        <div className="border-1 border-solid border-alert py-4px px-8px text-alert radius-8px fs-13 fw-400">
                          Pending Review
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-3 min-w-130px">
                        <div className="fs-10 fw-400 text-grey-medium">
                          Enquiry Submitted
                        </div>
                        <div className="fs-13 fw-400 text-black">
                          {convertDate(getAllPendingEnquiry.created_at)}
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-3 min-w-180px w-180px mobile-w-100 text-break">
                        <div className="fs-10 fw-400 text-grey-medium">
                          Primary Contact
                        </div>
                        <div className="fs-13 fw-400 text-black">
                          {getAllPendingEnquiry?.first_name}{" "}
                          {getAllPendingEnquiry?.last_name}
                          <span className="text-grey-medium">
                            {getAllPendingEnquiry?.phone_number &&
                              `(${getAllPendingEnquiry.phone_number})`}
                          </span>
                        </div>
                      </div>
                      <CustomButton
                        variant={"primary"}
                        type="button"
                        className="btn-theme bg-teal text-black mobile-w-100"
                        onClick={() =>
                          handleReviewEnquiry(getAllPendingEnquiry?.id)
                        }
                      >
                        Review
                        <RightArrow color={"#000000"} />
                      </CustomButton>
                    </div>
                  )
                )
              ) : (
                <div className="d-flex flex-column align-items-center text-grey-medium">
                  No Pending Reviews Found
                </div>
              )}

              {totalPendingCentresCount > DEFAULT_ITEMS_PER_PAGE && (
                <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="fs-13 text-black text-nowrap">
                      Items per page
                    </div>
                    <div className="form-group">
                      <select
                        name=""
                        id=""
                        className="form-select text-only"
                        onChange={(e) => {
                          isInitialLoad.current = false;
                          setPendingReviewPerPage(Number(e.target.value));
                          setPendingReviewPage(1);
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                          return (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <Pagination
                    currentPage={pendingReviewPage}
                    totalPages={totalPendingReviewPage}
                    paginationLinks={pendingReviewPaginationLinks}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    onPageChange={handlePendingReviewPageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="position-relative bg-teal-light py-8px px-15px radius-8px d-flex flex-column flex-lg-row gap-8 align-items-center justify-content-lg-between">
            <div
              className="d-flex gap-8 align-items-center cursor-pointer"
              onClick={() => setisOpen2(!isOpen2)}
            >
              <Chevron isOpen={isOpen2} />
              <div className="fs-16 fw-700 text-black">
                {totalActiveCentresCount > 0 ? totalActiveCentresCount : 0}{" "}
                Active Centres
              </div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div className="fs-13 text-black text-nowrap">Sort By</div>
              <div className="form-group">
                <SortBySelect
                  // className="form-select text-only min-h-auto fw-700"
                  options={CENTRE_SORT_OPTIONS}
                  onChange={handleSorting}
                  onSelect={handleSelect}
                  enableFilter={false}
                  defaultValue={CENTRE_SORT_OPTIONS[0]}
                />
              </div>
            </div>
          </div>
          {isOpen2 && (
            <div className="d-flex flex-column">
              {getAllActiveCentres && getAllActiveCentres?.data.length > 0 ? (
                getAllActiveCentres !== null &&
                getAllActiveCentres?.data?.map(
                  (getAllActiveCentre: TCenterDetails, index: number) => (
                    <>
                      <div
                        key={index}
                        className={`bg-white p-15px d-flex gap-20 gap-mobile-10 align-items-start align-items-lg-center justify-content-between fs-13 fw-400 text-black ${
                          totalActiveCentresCount > 1
                            ? "border-bottom border-off-white"
                            : ""
                        } `}
                      >
                        <div className="d-flex flex-column flex-lg-row gap-40 gap-mobile-10 align-items-start align-items-lg-center w-100">
                          <div className="d-flex gap-20 align-items-center flex-grow-1 text-break">
                            {getAllActiveCentre?.photo === null ||
                            getAllActiveCentre?.photo?.length === 0 ? (
                              <PlaceHolderImage />
                            ) : (
                              <div className="w-50px h-50px radius-50px">
                                <img
                                  src={`${getAllActiveCentre?.photo}`}
                                  alt="Centre"
                                  width={"50px"}
                                  height={"50px"}
                                  className="img-responsive rounded-circle object-fit-cover"
                                />
                              </div>
                            )}

                            <div className="flex-grow-1 d-flex flex-column gap-3 text-black">
                              <div className="fs-16 fw-700 lh-16px">
                                {getAllActiveCentre.business_name}
                              </div>
                              <div className="fs-13 fw-400">
                                {getAllActiveCentre.address}
                              </div>
                            </div>
                          </div>
                          <div className="min-w-190px w-190px mobile-w-100 d-flex flex-column gap-3 text-break">
                            <div className="fs-10 fw-400 text-grey-medium">
                              Primary Contact
                            </div>
                            <div className="fs-13 fw-400 text-black">
                              {getAllActiveCentre?.user?.first_name}{" "}
                              {getAllActiveCentre?.user?.last_name}
                              <span className="text-grey-medium">
                                {getAllActiveCentre?.phone_number &&
                                  `(${getAllActiveCentre.phone_number})`}
                              </span>
                            </div>
                          </div>
                          <div className="min-w-190px d-flex flex-column gap-3">
                            <div className="fs-10 fw-400 text-grey-medium">
                              Current Pending
                            </div>
                            <div className="fs-13 fw-400 text-black">
                              {getAllActiveCentre.open_vacancies_count} Vacancies Total{" "}
                              { getAllActiveCentre.urgent_vacancies_count ? <span className="text-alert">({getAllActiveCentre.urgent_vacancies_count ?? 0} Urgent)</span> : "" }
                            </div>
                          </div>
                          <div className="min-w-90px d-flex flex-column gap-3">
                            <div className="fs-10 fw-400 text-grey-medium">
                              Filled and Historic
                            </div>
                            <div className="fs-13 fw-400 text-black">
                              {getAllActiveCentre.filled_vacancies_count} Vacancies
                            </div>
                          </div>
                          <div className="min-w-90px d-flex flex-column gap-3">
                            <div className="fs-10 fw-400 text-grey-medium">
                              Cancellations
                            </div>
                            <div className="fs-13 fw-400 text-black">
                              {getAllActiveCentre.cancelled_vacancies_count} Vacancy
                            </div>
                          </div>
                        </div>
                        <div className="position-relative">
                          <CustomButton
                            variant={"primary"}
                            className="btn-theme bg-white p-0 h-auto"
                            onClick={() => {
                              setOpenDropdown(!openDropdown);
                              setEditCentreId(getAllActiveCentre?.id);
                            }}
                          >
                            <VerticalDots />
                          </CustomButton>
                          {openDropdown &&
                            editCentreId === getAllActiveCentre?.id && (
                              <div
                                className="position-absolute end-0 d-flex flex-column dropdown-menu"
                                ref={dropdownRef}
                              >
                                <div
                                  className="dropdown-item cursor-pointer"
                                  onClick={() => handleEdit()}
                                >
                                  <EditPencil />
                                  Edit Centre
                                </div>
                                <div
                                  className="dropdown-item cursor-pointer"
                                  onClick={() =>
                                    handleView(getAllActiveCentre.id)
                                  }
                                >
                                  <View24 />
                                  View Centre
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </>
                  )
                )
              ) : (
                <div className="d-flex flex-column align-items-center text-grey-medium">
                  No Active Centres found
                </div>
              )}

              {totalActiveCentresCount > DEFAULT_ITEMS_PER_PAGE && isOpen2 && (
                <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap pt-20px">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="fs-13 text-black text-nowrap">
                      Items per page
                    </div>
                    <div className="form-group">
                      <select
                        name=""
                        id=""
                        className="form-select text-only"
                        onChange={(e) => {
                          isInitialLoad.current = false;
                          setActiveReviewPerPage(Number(e.target.value));
                          setActiveReviewPage(1);
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                          return (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <Pagination
                    currentPage={activeReviewPage}
                    totalPages={totalActiveReviewPage}
                    paginationLinks={activeReviewPaginationLinks}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    onPageChange={handleActiveReviewPageChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Review Enquiry Popup Modal */}
      <ReviewCentre
        showModal={showModal}
        setShowModal={setShowModal}
        pendingReviewPage={pendingReviewPage}
        pendingReviewPerPage={pendingReviewPerPage}
        selectedKeyword={selectedKeyword}
      />
      {/* End - Review Enquiry Popup Modal */}

      {/* View Centre Popup Modal */}
      <ViewCentre
        showViewModal={showViewModal}
        setShowViewModal={setShowViewModal}
      />
      {/* End - View Centre Popup Modal */}
    </>
  );
};

export default AdminCentres;
